import {ConnectedOverlayPositionChange, ConnectedPosition} from '@angular/cdk/overlay';

/**
 * MAP-1583 (review round 2): the AI bubbles used to be `position: absolute`
 * children of the sidebar, which clips them. Both sidebar panels scroll their
 * body (`.main-content` in sidebar-form.component.scss / map-info-section
 * .component.scss) and the drawer itself is `overflow: hidden`
 * (dashboard.component.scss `.drawer`), so a bubble opened from a row near the
 * bottom of the panel was sliced off at the panel edge with no way to reveal
 * it.
 *
 * The bubbles now render through a CDK connected overlay, which puts them in
 * the body-level `.cdk-overlay-container` (outside every clipping ancestor)
 * and picks the first of these positions that actually fits the viewport:
 * below the trigger when there is room, above it otherwise. The tip has to
 * follow, hence `isBubbleAbove()` -> the `arrow-down` host class.
 *
 * The 7px horizontal nudge and the 8px gap reproduce the offsets the absolute
 * positioning used, so the tip still lands on the trigger.
 */
const GAP = 8;
const EDGE_NUDGE = 7;

/** Trigger sits at the RIGHT edge of the panel: bubble hangs off its end. */
export const AI_BUBBLE_POSITIONS_END: ConnectedPosition[] = [
  {originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetX: EDGE_NUDGE, offsetY: GAP},
  {originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetX: EDGE_NUDGE, offsetY: -GAP},
];

/** Trigger sits at the LEFT edge of the panel: bubble hangs off its start. */
export const AI_BUBBLE_POSITIONS_START: ConnectedPosition[] = [
  {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetX: -EDGE_NUDGE, offsetY: GAP},
  {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetX: -EDGE_NUDGE, offsetY: -GAP},
];

/**
 * True when the overlay flipped above its trigger, i.e. it is anchored to the
 * trigger's TOP edge, so the tip belongs at the bubble's bottom pointing down.
 */
export function isBubbleAbove(change: ConnectedOverlayPositionChange): boolean {
  return change.connectionPair.originY === 'top';
}
